import type { AppStartupType } from '@snapchat/blizzard-schema';
import { getLogger } from '../logger';
import { generateBase64UUId } from './../util/strings';

const log = getLogger('session');

/**
 * Properties associated with this session. A session is defined as app foreground (/focus) until
 * the next app foreground.
 *
 * @internal
 */
export interface SessionProperties {
  sessionId: string;
  appStartupType: AppStartupType;
}

let sessionProperties: SessionProperties | undefined;

/**
 * Get the current {@link SessionProperties}. Lazily creates a new session if one hasn't been
 * started.
 *
 * @internal
 */
export function getSessionProperties(): SessionProperties | undefined {
  if (sessionProperties === undefined) {
    startNewSession('COLD');
  }
  return sessionProperties;
}

/**
 * Test-only method to set the session properties.
 *
 * @internal
 */
export function setSessionPropertiesForTest(properties?: SessionProperties): void {
  sessionProperties = properties;
}

/**
 * Explicitly start a new session.
 *
 * @internal
 */
export function startNewSession(appStartupType: AppStartupType): void {
  const props: SessionProperties = {
    // Generate session ID - 12 bytes of entropy per Android implementation, base64 encoded
    // https://docs.google.com/document/d/1mrtld8oOxF3yCHwF8ZXqHrzfIJDDIq4d0S-taDRV-kQ/view
    sessionId: generateBase64UUId(),

    // Flip appStartupType to WARM if this isn't the first resume in this session.
    appStartupType,
  };
  log.debug('Started new Blizzard session:', props);
  sessionProperties = props;
}
